import React, { useMemo } from 'react';
import styles from './TicketInfo.module.scss';
import moment from 'moment';
import BetLineStatus from './BetLineStatus/BetLineStatus';
import LiveBadge from '../../../../LiveBadge/LiveBadge';
import { TICKET_TYPES } from '../../../../../../constants/defaultValues';
import { getTranslation } from '../../../../../../utils/translation';
import { EVENT_TYPE_ENUM } from '../../../../../../constants/enums';

export default function TicketInfo({ ticket }) {
    const isBettingScoreVisible = useMemo(() => ticket.betLines?.find(betLine => betLine.live), [ticket]);

    return (
        <div className={styles.ticketInfo}>
            <h4 className={styles.title}>{getTranslation('ticketInformationTitle')}</h4>
            <div className='flex'>
                <table className={styles.infoTable}>
                    <tbody>
                        <tr>
                            <th align='left'>{getTranslation('ticketInformationTerminalNameColumn')}</th>
                            <td align='right'>Cashier</td>
                        </tr>
                        <tr>
                            <th align='left'>{getTranslation('ticketInformationBetId')}</th>
                            <td align='right'>{ticket.ticketId}</td>
                        </tr>
                        <tr>
                            <th align='left'>{getTranslation('ticketInformationDateColumn')}</th>
                            <td align='right'>{moment(ticket.issuedDate).format('DD MMMM YYYY HH:mm:ss')}</td>
                        </tr>
                        <tr>
                            <th align='left'>{getTranslation('ticketInformationNumberColumn')}</th>
                            <td align='right'>{ticket.code}</td>
                        </tr>
                        <tr>
                            <th align='left'>{getTranslation('ticketInformationTotalOddsColumn')}</th>
                            <td align='right'>{ticket.odds.toFixed(2)}</td>
                        </tr>
                        <tr>
                            <th align='left'>{getTranslation('ticketInformationTotalCostColumn')} </th>
                            <td align='right'>{ticket.totalStakeAmount.toFixed(2)} AZN</td>
                        </tr>
                        <tr>
                            <th align='left'>{getTranslation('ticketInformationStakePerBetColumn')}</th>
                            <td align='right'>{ticket.stakePerBet > 0 ? ticket.stakePerBet.toFixed(2) : '-'}</td>
                        </tr>
                    </tbody>
                </table>
                <table className={styles.infoTable}>
                    <tbody>
                        <tr>
                            <th align='left'>{getTranslation('betSlip.placeBets.bonus')}</th>
                            <td align='right' className={styles.green}>
                                {ticket.totalBonusAmount.toFixed(2)} AZN
                            </td>
                        </tr>
                        <tr>
                            <th align='left'>{getTranslation('ticketInformationToWinColumn')}</th>
                            <td align='right' className={styles.green}>
                                {ticket.totalWinAmount.toFixed(2)} AZN
                            </td>
                        </tr>
                        <tr>
                            <th align='left'>{getTranslation('ticketInformationWinTaxColumn')}</th>
                            <td align='right' className={styles.red}>
                                {ticket.winTax.toFixed(2)} AZN
                            </td>
                        </tr>
                        <tr>
                            <th align='left'>{getTranslation('ticketInformationToPayColumn')}</th>
                            <td align='right' className={styles.green}>
                                {ticket.totalPayAmount.toFixed(2)} AZN
                            </td>
                        </tr>
                        <tr>
                            <th align='left'>{getTranslation('ticketInformationPaidDateColumn')}</th>
                            <td align='right'>
                                {ticket.paidAt !== 0 ? moment(ticket.paidAt).format('DD MMMM YYYY HH:mm:ss') : '-'}
                            </td>
                        </tr>
                        <tr>
                            <th align='left'>{getTranslation('ticketInformationPaidColumn')}</th>
                            <td align='right'>{ticket.paidAt !== 0 ? `${ticket.payoutAmount.toFixed(2)} AZN` : '-'}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className={styles.betLinesWrap}>
                <table className={styles.betLinesTable}>
                    <thead>
                        <tr>
                            <th>{getTranslation('ticketInformationBetLineStatusColumn')}</th>
                            <th>{getTranslation('ticketInformationEventIdColumn')}</th>
                            {ticket.ticketType === TICKET_TYPES.system ? (
                                <th>{getTranslation('ticketInformationBetLineBancoColumn')}</th>
                            ) : null}
                            <th>{getTranslation('ticketInformationBetLineDateColumn')}</th>
                            <th>{getTranslation('ticketInformationBetLineEventColumn')}</th>
                            <th>{getTranslation('ticketInformationBetLineLeagueColumn')}</th>
                            {isBettingScoreVisible && (
                                <th align='center'>{getTranslation('ticketInformationBetLineScoreColumn')}</th>
                            )}
                            <th align='center'>{getTranslation('ticketInformationBetLineMatchScoreColumn')}</th>
                            <th>{getTranslation('ticketInformationBetLineSelectionColumn')}</th>
                            <th>{getTranslation('ticketInformationBetLineOddsColumn')}</th>
                            <th>{getTranslation('ticketInformationBetLineDescriptionColumn')}</th>
                            {ticket.ticketType === TICKET_TYPES.single ? (
                                <th>{getTranslation('ticketInformationBetLineStakeColumn')}</th>
                            ) : null}
                        </tr>
                    </thead>

                    <tbody>
                        {ticket.betLines.map((betLine, index) => (
                            <tr key={index}>
                                <td>
                                    <BetLineStatus statusId={betLine.status} />
                                </td>
                                <td>
                                    <span className={styles.eventId}>
                                        {betLine.eventType !== EVENT_TYPE_ENUM.outright ? betLine.programCode : '-'}
                                    </span>
                                </td>
                                {ticket.ticketType === TICKET_TYPES.system ? (
                                    <td>{betLine.banco ? <span className={styles.banco}>B</span> : null}</td>
                                ) : null}
                                <td>{betLine.kickStartDate}</td>
                                <td className={styles.eventName}>
                                    <span>{betLine.event.split('-')[0]}</span>
                                    <span>{betLine.event.split('-')[1]}</span>
                                    <LiveBadge isLive={betLine.live} />
                                </td>
                                <td className={styles.leagueName}>{betLine.league}</td>
                                {isBettingScoreVisible && <td align='center'>{betLine.bettingScore}</td>}
                                <td align='center'>{betLine.matchScore}</td>
                                <td>{betLine.outcomeName}</td>
                                <td>{betLine.odds}</td>
                                <td>{betLine.marketName}</td>
                                {ticket.ticketType === TICKET_TYPES.single ? (
                                    <td>{betLine.stakeAmount.toFixed(2)} AZN</td>
                                ) : null}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
